import { IconProps } from "@velaro/velaro-component-lib";
import React from "react";
import { Link } from "react-router-dom";

export function Card({
  to,
  title,
  subtitle,
  icon,
  status,
  variant = "default",
  isFirstCard = false,
  onClick,
  appStatus,
  locked = false,
  "data-testid": dataTestId
}: {
  to: string;
  title: string;
  subtitle: string;
  icon?: string | React.FC<IconProps>;
  status?: string;
  variant?: "default" | "integrations";
  isFirstCard?: boolean;
  onClick?: () => void;
  appStatus?: string;
  /**
   * When true the card renders in "locked" state:
   *   - Amber lock badge replaces the status badge
   *   - Muted border + icon opacity indicates unavailability
   *   - "Upgrade" CTA replaces the "Configure →" hover hint
   *   - onClick should open the UpgradeRequestPanel, not navigate
   *
   * New integrations: set this via the integrationGateMap in IntegrationsLanding.tsx.
   * No other code changes needed — Card handles the locked UI automatically.
   */
  locked?: boolean;
  "data-testid"?: string;
}) {
  if (variant === "integrations") {
    if (isFirstCard) {
      return (
        <div className="w-full h-full cursor-pointer" onClick={onClick}>
          <div className="h-full min-h-[192px] p-6 bg-white rounded-xl border-2 border-dashed border-slate-300 w-full hover:border-cornflower-blue-400 hover:bg-cornflower-blue-50 transition-all flex flex-col items-center justify-center gap-3 group">
            <div className="w-12 h-12 rounded-full bg-slate-100 group-hover:bg-cornflower-blue-100 flex items-center justify-center transition-colors">
              {icon && typeof icon !== "string" && React.createElement(icon, { className: "w-5 h-5 text-slate-500 group-hover:text-cornflower-blue-600" })}
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-slate-700 group-hover:text-cornflower-blue-700">{title}</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-[200px]">{subtitle}</p>
            </div>
          </div>
        </div>
      );
    }

    const isConnected = !locked && appStatus === "Connected";

    return (
      <div className="w-full h-full cursor-pointer" onClick={onClick} data-testid={dataTestId}>
        <div
          className={`h-full min-h-[200px] rounded-xl border w-full transition-all flex flex-col p-5 gap-4 group relative overflow-hidden
            ${locked
              ? "bg-slate-50 border-slate-200 hover:border-amber-300 hover:shadow-sm"
              : "bg-white border-slate-200 hover:border-cornflower-blue-300 hover:shadow-md"
            }`}
        >
          {/* Connected indicator stripe — only when connected and unlocked */}
          {isConnected && (
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-green-400 rounded-t-xl" />
          )}

          {/* Locked amber top stripe */}
          {locked && (
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-amber-300 rounded-t-xl" />
          )}

          {/* Header row: logo + badge */}
          <div className="flex items-start justify-between">
            {icon && (
              <div className={`w-14 h-14 rounded-xl border bg-slate-50 flex items-center justify-center p-2 shadow-sm flex-shrink-0 transition-opacity
                ${locked ? "border-slate-100 opacity-50" : "border-slate-100"}`}>
                {typeof icon === "string" ? (
                  <img src={icon} alt="" className="w-full h-full object-contain" />
                ) : (
                  React.createElement(icon, { className: "w-7 h-7" })
                )}
              </div>
            )}

            {/* Locked badge */}
            {locked && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Upgrade
              </span>
            )}

            {/* Connected / Pending badge — only when unlocked */}
            {!locked && appStatus && appStatus !== "" && (
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 ${
                  isConnected
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-slate-100 text-slate-500 border border-slate-200"
                }`}
              >
                {isConnected && (
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                )}
                {appStatus}
              </span>
            )}
          </div>

          {/* Name + subtitle */}
          <div className="flex-1 min-h-0">
            <h3 className={`font-semibold text-sm leading-snug transition-colors
              ${locked
                ? "text-slate-500 group-hover:text-amber-700"
                : "text-slate-800 group-hover:text-cornflower-blue-700"
              }`}>{title}</h3>
            <p className="text-xs text-slate-400 mt-1 line-clamp-3 leading-relaxed">{subtitle}</p>
          </div>

          {/* Bottom CTA hint on hover */}
          <div className="flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
            {locked ? (
              <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                Contact us to unlock
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </span>
            ) : (
              <span className="text-xs text-cornflower-blue-500 font-medium flex items-center gap-1">
                Configure
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </span>
            )}
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="w-80 h-36">
        <Link to={to}>
          <div className="border float-left rounded w-80 h-36 mr-4 mb-4 hover:shadow group">
            <div className="font-semibold mb-2 bg-cornflower-blue-400 group-hover:bg-cornflower-blue-500 text-slate-50 p-3 group">
              {title}
            </div>
            <div className="text-sm p-3">{subtitle}</div>
          </div>
        </Link>
      </div>
    );
  }
}
